import { useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MergeIcon from "@mui/icons-material/MergeType";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import type {
  AdminDataPermissionInfo,
  AdminFormContextInfo,
  AdminGetDataPermissionInfoResponse,
  AdminWorkItemInfo,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import {
  Button,
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

type ResourceType = "patient" | "form-context";

interface DeleteConfirmation {
  type: "workItem" | "formContextPermission" | "patientPermission";
  workItemId?: string;
  formContextId?: string;
  resourceType?: ResourceType;
  resourceId?: string;
  dataControllerId?: string;
  dataControllerName?: string;
  teamName?: string;
  willOrphan?: boolean;
  orphanType?: "noWorkItems" | "noPermissions";
}

export function DataPermissionManagementPage() {
  const [resourceType, setResourceType] = useState<ResourceType>("patient");
  const [resourceId, setResourceId] = useState("");
  const [ticketNumber, setTicketNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<AdminGetDataPermissionInfoResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [expandedFormContexts, setExpandedFormContexts] = useState<Set<string>>(
    new Set(),
  );
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmation | null>(null);
  const [orphanConfirmed, setOrphanConfirmed] = useState(false);

  const handleSearch = async () => {
    if (!resourceId.trim()) {
      setError("Please enter a resource ID");
      return;
    }

    setIsLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await api.admin.getDataPermissionInfo(
        resourceType,
        resourceId.trim(),
      );
      if (response.status === 200) {
        setData(response.data);
        // Expand all form contexts by default
        if (response.data.formContexts) {
          setExpandedFormContexts(
            new Set(response.data.formContexts.map((fc) => fc.formContextId)),
          );
        }
      } else {
        setError("Failed to fetch data permission info");
      }
    } catch {
      setError("An error occurred while fetching data");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFormContext = (formContextId: string) => {
    setExpandedFormContexts((prev) => {
      const next = new Set(prev);
      if (next.has(formContextId)) {
        next.delete(formContextId);
      } else {
        next.add(formContextId);
      }
      return next;
    });
  };

  const handleDeleteWorkItem = async (workItemId: string) => {
    if (!ticketNumber.trim()) {
      renderErrorToast({ message: "Ticket number is required" });
      return;
    }

    try {
      const response = await api.admin.deleteWorkItem(
        workItemId,
        `Admin deletion via Data Permission Management - Ticket: ${ticketNumber}`,
        ticketNumber.trim(),
      );
      if (response.status === 200) {
        renderSuccessToast({ message: "Work item deleted successfully" });
        void handleSearch(); // Refresh data
      } else {
        const errorData = response.data as unknown as { reason?: string };
        renderErrorToast({
          message: errorData.reason ?? "Failed to delete work item",
        });
      }
    } catch {
      renderErrorToast({ message: "An error occurred" });
    }
    setDeleteConfirmation(null);
  };

  const handleDeleteDataPermission = async (
    targetResourceType: ResourceType,
    targetResourceId: string,
    dataControllerId: string,
  ) => {
    if (!ticketNumber.trim()) {
      renderErrorToast({ message: "Ticket number is required" });
      return;
    }

    try {
      const response = await api.admin.deleteDataPermission(
        targetResourceType,
        targetResourceId,
        dataControllerId,
        ticketNumber.trim(),
      );
      if (response.status === 204) {
        renderSuccessToast({ message: "Data permission deleted successfully" });
        void handleSearch(); // Refresh data
      } else {
        const errorData = response.data as unknown as { reason?: string };
        renderErrorToast({
          message: errorData.reason ?? "Failed to delete data permission",
        });
      }
    } catch {
      renderErrorToast({ message: "An error occurred" });
    }
    setDeleteConfirmation(null);
  };

  const canDeleteFormContextPermission = (
    workItems: AdminWorkItemInfo[],
    dataControllerId: string,
  ): boolean => {
    // Deletion is ordered per data controller: its own work items must go before
    // its permission.
    return !workItems.some((wi) => wi.dataControllerId === dataControllerId);
  };

  const canDeletePatientPermission = (dataControllerId: string): boolean => {
    if (!data?.formContexts) return true;
    // Can only delete if no form context permissions exist for this data controller
    return !data.formContexts.some((fc) =>
      fc.dataPermissions.some((dp) => dp.dataControllerId === dataControllerId),
    );
  };

  const soleElement = <T,>(items: T[]): T | undefined =>
    items.length === 1 ? items[0] : undefined;

  // Check if deleting this work item would leave the form context with no work items
  const willOrphanFormContextWorkItems = (
    formContextId: string,
    workItemId: string,
  ): boolean => {
    if (data?.resourceType === "form-context") {
      // Direct form context view
      return soleElement(data.workItems)?.workItemId === workItemId;
    }
    // Patient view - check the specific form context
    const fc = data?.formContexts?.find(
      (f) => f.formContextId === formContextId,
    );
    if (!fc) return false;
    return soleElement(fc.workItems)?.workItemId === workItemId;
  };

  // Check if deleting this permission would leave the form context with no permissions
  const willOrphanFormContextPermissions = (
    formContextId: string,
    dataControllerId: string,
  ): boolean => {
    if (data?.resourceType === "form-context") {
      // Direct form context view
      return (
        soleElement(data.dataPermissions)?.dataControllerId === dataControllerId
      );
    }
    // Patient view - check the specific form context
    const fc = data?.formContexts?.find(
      (f) => f.formContextId === formContextId,
    );
    if (!fc) return false;
    return (
      soleElement(fc.dataPermissions)?.dataControllerId === dataControllerId
    );
  };

  // Check if deleting this patient permission would leave the patient with no permissions
  const willOrphanPatientPermissions = (dataControllerId: string): boolean => {
    if (!data || data.resourceType !== "patient") return false;
    return (
      soleElement(data.dataPermissions)?.dataControllerId === dataControllerId
    );
  };

  const renderWorkItemsTable = (
    workItems: AdminWorkItemInfo[],
    isDisabled: boolean,
    formContextId?: string,
  ) => {
    if (workItems.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            py: 1,
          }}
        >
          No work items
        </Typography>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Work Item ID</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data Controller</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workItems.map((wi) => {
              const fcId = formContextId ?? wi.formContextId;
              const willOrphan = willOrphanFormContextWorkItems(
                fcId,
                wi.workItemId,
              );
              return (
                <TableRow key={wi.workItemId}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {wi.workItemId}
                    </Typography>
                  </TableCell>
                  <TableCell>{wi.teamName}</TableCell>
                  <TableCell>
                    <Chip label={wi.status} size="small" />
                  </TableCell>
                  <TableCell>{wi.dataControllerName}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        isDisabled
                          ? "Enter a ticket number to enable deletion"
                          : willOrphan
                            ? "Warning: This is the last work item - form context will have no work items"
                            : "Delete work item"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isDisabled}
                          onClick={() => {
                            setDeleteConfirmation({
                              type: "workItem",
                              workItemId: wi.workItemId,
                              formContextId: fcId,
                              teamName: wi.teamName,
                              dataControllerName: wi.dataControllerName,
                              willOrphan,
                              orphanType: willOrphan
                                ? "noWorkItems"
                                : undefined,
                            });
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDataPermissionsTable = (
    permissions: AdminDataPermissionInfo[],
    targetResourceType: ResourceType,
    targetResourceId: string,
    canDeleteFn: (dataControllerId: string) => boolean,
    isDisabled: boolean,
    formContextId?: string,
  ) => {
    if (permissions.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            py: 1,
          }}
        >
          No data permissions
        </Typography>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data Controller ID</TableCell>
              <TableCell>Data Controller Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((dp) => {
              const canDelete = canDeleteFn(dp.dataControllerId);
              const willOrphan =
                targetResourceType === "patient"
                  ? willOrphanPatientPermissions(dp.dataControllerId)
                  : willOrphanFormContextPermissions(
                      formContextId ?? targetResourceId,
                      dp.dataControllerId,
                    );
              return (
                <TableRow key={dp.dataControllerId}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {dp.dataControllerId}
                    </Typography>
                  </TableCell>
                  <TableCell>{dp.dataControllerName}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        isDisabled
                          ? "Enter a ticket number to enable deletion"
                          : !canDelete
                            ? targetResourceType === "patient"
                              ? "Delete form context permissions for this data controller first"
                              : "Delete work items for this data controller first"
                            : willOrphan
                              ? `Warning: This is the last permission - ${targetResourceType} will be orphaned`
                              : "Delete data permission"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isDisabled || !canDelete}
                          onClick={() => {
                            setDeleteConfirmation({
                              type:
                                targetResourceType === "patient"
                                  ? "patientPermission"
                                  : "formContextPermission",
                              resourceType: targetResourceType,
                              resourceId: targetResourceId,
                              formContextId,
                              dataControllerId: dp.dataControllerId,
                              dataControllerName: dp.dataControllerName,
                              willOrphan,
                              orphanType: willOrphan
                                ? "noPermissions"
                                : undefined,
                            });
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderFormContext = (fc: AdminFormContextInfo) => {
    const isExpanded = expandedFormContexts.has(fc.formContextId);
    const isDisabled = !ticketNumber.trim();

    return (
      <Paper key={fc.formContextId} sx={{ mb: 2, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            bgcolor: "grey.100",
            cursor: "pointer",
          }}
          onClick={() => {
            toggleFormContext(fc.formContextId);
          }}
        >
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
            Form Context: {fc.formContextId}
          </Typography>
          <Box sx={{ ml: 2, display: "flex", gap: 1 }}>
            <Chip
              label={`${fc.workItems.length} work items`}
              size="small"
              color={fc.workItems.length > 0 ? "warning" : "default"}
            />
            <Chip
              label={`${fc.dataPermissions.length} permissions`}
              size="small"
            />
          </Box>
        </Box>
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Work Items
            </Typography>
            {renderWorkItemsTable(fc.workItems, isDisabled, fc.formContextId)}

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Data Permissions
            </Typography>
            {renderDataPermissionsTable(
              fc.dataPermissions,
              "form-context",
              fc.formContextId,
              (dataControllerId) =>
                canDeleteFormContextPermission(fc.workItems, dataControllerId),
              isDisabled,
              fc.formContextId,
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  };

  const isDeleteDisabled = !ticketNumber.trim();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Data Permission Management
      </Typography>
      <Alert severity="warning" sx={{ mb: 3 }}>
        This tool allows you to remove data permissions and work items from
        patients and form contexts. Use with caution - these actions are
        audited. You must delete work items before removing form context
        permissions, and form context permissions before removing patient
        permissions for a given data controller.
      </Alert>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Search
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <RadioGroup
            row
            value={resourceType}
            onChange={(e) => {
              setResourceType(e.target.value as ResourceType);
            }}
          >
            <FormControlLabel
              value="patient"
              control={<Radio />}
              label="Patient"
            />
            <FormControlLabel
              value="form-context"
              control={<Radio />}
              label="Form Context"
            />
          </RadioGroup>

          <TextField
            label="Resource ID (UUID)"
            value={resourceId}
            onChange={(e) => {
              setResourceId(e.target.value);
            }}
            sx={{ minWidth: 350 }}
            placeholder="Enter patient or form context ID"
          />

          <Button
            label="Search"
            onClick={handleSearch}
            disabled={isLoading || !resourceId.trim()}
            customStartIcon={<SearchIcon />}
          />
        </Box>

        <TextField
          label="Ticket Number (Required for deletions)"
          value={ticketNumber}
          onChange={(e) => {
            setTicketNumber(e.target.value);
          }}
          sx={{ minWidth: 300 }}
          placeholder="e.g. CCM-1234"
          helperText="A ticket number is required before any deletions can be performed"
        />
      </Paper>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <LoadingSpinner />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {data && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="h6">
              Results for {data.resourceType}: {data.resourceId}
            </Typography>
            {data.resourceType === "patient" && data.isMerged && (
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Merged Patient
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Root Patient ID: {data.rootPatientId}
                      <IconButton
                        size="small"
                        onClick={() => {
                          void navigator.clipboard.writeText(
                            data.rootPatientId ?? "",
                          );
                          renderSuccessToast({
                            message: "Copied root patient ID",
                          });
                        }}
                        sx={{ ml: 0.5, p: 0.25 }}
                      >
                        <ContentCopyIcon
                          sx={{ fontSize: 14, color: "white" }}
                        />
                      </IconButton>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      All Patient IDs in merge tree:
                    </Typography>
                    {data.mergedPatientIds?.map((id) => (
                      <Box
                        key={id}
                        sx={{ display: "flex", alignItems: "center", ml: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontSize: 12 }}
                        >
                          {id}
                          {id === data.rootPatientId && " (root)"}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            void navigator.clipboard.writeText(id);
                            renderSuccessToast({
                              message: "Copied patient ID",
                            });
                          }}
                          sx={{ ml: 0.5, p: 0.25 }}
                        >
                          <ContentCopyIcon
                            sx={{ fontSize: 14, color: "white" }}
                          />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                }
                arrow
                placement="right"
              >
                <Chip
                  icon={<MergeIcon />}
                  label={`Merged (${data.mergedPatientIds?.length ?? 0} patients)`}
                  color="info"
                  size="small"
                  sx={{ cursor: "pointer" }}
                />
              </Tooltip>
            )}
          </Box>

          {data.resourceType === "patient" && (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Patient Data Permissions
                </Typography>
                {renderDataPermissionsTable(
                  data.dataPermissions,
                  "patient",
                  data.resourceId,
                  canDeletePatientPermission,
                  isDeleteDisabled,
                )}
              </Paper>

              {data.formContexts && data.formContexts.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Form Contexts ({data.formContexts.length})
                  </Typography>
                  {data.formContexts.map(renderFormContext)}
                </>
              )}

              {data.formContexts && data.formContexts.length === 0 && (
                <Alert severity="info">
                  No form contexts found for this patient
                </Alert>
              )}
            </>
          )}

          {data.resourceType === "form-context" && (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Work Items
                </Typography>
                {renderWorkItemsTable(
                  data.workItems,
                  isDeleteDisabled,
                  data.resourceId,
                )}
              </Paper>

              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Data Permissions
                </Typography>
                {renderDataPermissionsTable(
                  data.dataPermissions,
                  "form-context",
                  data.resourceId,
                  (dataControllerId) =>
                    canDeleteFormContextPermission(
                      data.workItems,
                      dataControllerId,
                    ),
                  isDeleteDisabled,
                  data.resourceId,
                )}
              </Paper>
            </>
          )}
        </Box>
      )}
      {deleteConfirmation && (
        <ConfirmationModal
          extraWarning={
            deleteConfirmation.willOrphan
              ? deleteConfirmation.orphanType === "noWorkItems"
                ? "⚠️ DANGER: This is the LAST work item. Deleting it will leave the form context with NO work items - it will become orphaned and no team will have it in their caseload."
                : deleteConfirmation.type === "patientPermission"
                  ? "⚠️ DANGER: This is the LAST data permission. Deleting it will leave the patient with NO data permissions - it will become completely orphaned and invisible to all data controllers."
                  : "⚠️ DANGER: This is the LAST data permission. Deleting it will leave the form context with NO data permissions - it will become completely orphaned and invisible to all data controllers."
              : "This action cannot be undone and will be audited."
          }
          message={
            <Box>
              <Typography sx={{ mb: 2 }}>
                {deleteConfirmation.type === "workItem"
                  ? `Are you sure you want to delete the work item for team "${deleteConfirmation.teamName}" (Data Controller: ${deleteConfirmation.dataControllerName})?`
                  : `Are you sure you want to remove the data permission for "${deleteConfirmation.dataControllerName}" from this ${deleteConfirmation.resourceType}?`}
              </Typography>
              {deleteConfirmation.willOrphan && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    This action will orphan the{" "}
                    {deleteConfirmation.type === "patientPermission"
                      ? "patient"
                      : "form context"}
                    !
                  </Typography>
                  <Typography variant="body2">
                    {deleteConfirmation.orphanType === "noWorkItems"
                      ? "No team will have access to this form context in their caseload after this deletion."
                      : "No data controller will have permission to access this resource after this deletion."}
                  </Typography>
                </Alert>
              )}
              {deleteConfirmation.willOrphan && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={orphanConfirmed}
                      onChange={(e) => {
                        setOrphanConfirmed(e.target.checked);
                      }}
                      color="error"
                    />
                  }
                  label={
                    <Typography
                      color="error"
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      I understand this will orphan the resource and confirm I
                      want to proceed
                    </Typography>
                  }
                />
              )}
            </Box>
          }
          confirmFn={() => {
            if (deleteConfirmation.willOrphan && !orphanConfirmed) {
              renderErrorToast({
                message:
                  "Please confirm you understand this will orphan the resource",
              });
              return;
            }
            if (deleteConfirmation.type === "workItem") {
              handleDeleteWorkItem(deleteConfirmation.workItemId!);
            } else {
              handleDeleteDataPermission(
                deleteConfirmation.resourceType!,
                deleteConfirmation.resourceId!,
                deleteConfirmation.dataControllerId!,
              );
            }
            setOrphanConfirmed(false);
          }}
          closeFn={() => {
            setDeleteConfirmation(null);
            setOrphanConfirmed(false);
          }}
        />
      )}
    </Box>
  );
}
